import type {
  AnchorHTMLAttributes,
  ComponentProps,
  MouseEventHandler,
  ReactNode,
} from 'react';
import { LinkBase } from 'ra-core';
import { cn } from '../../lib/utils';
import { Button, buttonVariants } from '../ui/button';
import { rowActionClasses, useIsInRowActions } from './RowActions';

type ButtonProps = ComponentProps<typeof Button>;

export interface ActionLinkProps
  extends Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    'children' | 'className' | 'onClick'
  > {
  to: string;
  children: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

/**
 * A react-admin link styled like the kit Button. Use this instead of manually
 * calling buttonVariants whenever a link is presented as an action. Inside `RowActions`, it gets the row action look.
 */
export function ActionLink({
  to,
  children,
  icon,
  disabled = false,
  variant = 'outline',
  size = 'default',
  className,
  onClick,
  ...props
}: ActionLinkProps) {
  const isRowAction = useIsInRowActions();
  const actionClassName = cn(
    buttonVariants({ variant, size }),
    isRowAction && rowActionClasses,
    className
  );

  if (disabled) {
    return (
      <span
        className={cn(
          actionClassName,
          'pointer-events-none border-border text-muted-foreground opacity-50'
        )}
        role="link"
        aria-disabled="true"
      >
        {icon}
        {children}
      </span>
    );
  }

  return (
    <LinkBase to={to} className={actionClassName} onClick={onClick} {...props}>
      {icon}
      {children}
    </LinkBase>
  );
}
